import React, { useState } from "react";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { MapPin, X } from "lucide-react";
import map from '../../assets/images/map.png';

const factories = [
  {
    id: 1,
    name: "Rudrapur",
    state: "Uttarakhand",
    position: { top: "20%", left: "36%" },
    color: "#FF6B6B",
    production: {
      products: ["LED TV", "Mobile-Phones", "Manpower"],
      capacity: ["200K", "500K", "1000+"],
      lines: ["5", "6", "NA"],
    }
  },
  {
    id: 2,
    name: "Bhiwadi",
    state: "Rajasthan",
    position: { top: "35%", left: "20%" },
    color: "#FF9F1C",
    production: {
      products: ["LED TV", "Mobile-Phones & Tablet PC", "PCB (SMT)", "Manpower"],
      capacity: ["200K", "600K", "500K (Mobile) / 200K (TV)", "2000+"],
      lines: ["4", "12", "4 Lines (2KK CPH)", "NA"],
    }
  },
  {
    id: 3,
    name: "Hyderabad",
    state: "Telangana",
    position: { top: "60%", left: "38%" },
    color: "#E76F51",
    production: {
      products: ["LED TV", "Mobile-Phones & Tablet PC", "PCB (SMT)", "Manpower"],
      capacity: ["200K", "600K", "100K (Mobile) / 100K (TV)", "2000+"],
      lines: ["4", "6", "1 Line (400K CPH)", "NA"],
    }
  },
  {
    id: 4,
    name: "Noida",
    state: "Uttar Pradesh",
    position: { top: "30%", left: "38%" },
    color: "#F4A261",
    production: {
      products: ["SMT Lines", "Smart Phones", "Manpower"],
      capacity: ["2.5M", "1.5M", "2600"],
      lines: ["12", "15", "NA"],
    }
  }
];

const LocationMarker = ({ location, isActive, onClick, onMouseEnter, onMouseLeave }) => (
  <motion.div
    className="absolute cursor-pointer"
    style={{ top: location.position.top, left: location.position.left }}
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
    onClick={onClick}
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ duration: 0.5 }}
  >
    <motion.div
      animate={{
        scale: isActive ? 1.2 : 1,
        transition: { type: "spring", stiffness: 300, damping: 20 }
      }}
      className="relative"
    >
      <div className="absolute -inset-2 bg-orange-500 rounded-full animate-ping opacity-20" />
      <MapPin 
        size={24} 
        className="text-orange-500 hover:text-orange-600 transition-colors relative z-10"
      />
    </motion.div>
    
    {/* Render LocationInfo directly in the marker component */}
    <AnimatePresence>
      {isActive && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 10 }}
          className="absolute z-20 w-48 bg-white/10 backdrop-blur-md p-4 rounded-lg border border-white/20"
          style={{
            left: '50%',
            transform: 'translateX(-50%)',
            top: 'calc(100% + 8px)', // Position below the marker
          }}
        >
          <h3 className="text-lg font-medium text-orange-500">
            {location.name}, {location.state}
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            Click to see details
          </p>
          {/* Add a pointer arrow */}
          <div 
            className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-white/10 backdrop-blur-md border-t border-l border-white/20"
            style={{
              transform: 'translateX(-50%) rotate(45deg)',
            }}
          />
        </motion.div>
      )}
    </AnimatePresence>
  </motion.div>
);


const FactoryDialog = ({ factory, onClose }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    transition={{ duration: 0.3, ease: "easeOut" }}
    className="fixed inset-0 flex items-center justify-center z-50 p-4 "
    onClick={onClose}
  >
    {/* Backdrop with smoother transition */}
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
      className="absolute inset-0 bg-black/40 backdrop-blur-md "
    />
    
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ 
        duration: 0.3,
        ease: "easeOut",
        scale: {
          type: "spring",
          damping: 25,
          stiffness: 300
        }
      }}
      className="relative w-full max-w-2xl overflow-hidden"
      onClick={e => e.stopPropagation()}
    >
      {/* Static gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/30 to-white/10 rounded-2xl" />
      <div className="absolute inset-0 backdrop-blur-xl rounded-2xl" />
      
      {/* Content container */}
      <div className="relative rounded-2xl border border-white/30 shadow-2xl 
                    bg-white/30 backdrop-blur-md overflow-hidden">
        
        {/* Header section */}
        <div className="p-6 bg-gradient-to-r from-orange-500/10 via-orange-500/5 to-transparent 
                     border-b border-white/20">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-full 
                     hover:bg-black/5 transition-colors duration-200
                     text-gray-600 hover:text-gray-800"
          >
            <X size={20} />
          </button>
          
          <h2 className="text-3xl font-bold text-gray-800">
            {factory.name}
          </h2>
          <p className="text-gray-600 mt-1">{factory.state}</p>
        </div>
        
        {/* Main content */}
        <div className="p-6 space-y-6">
          <div className="grid grid-cols-3 gap-6">
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-800 text-lg pb-2">
                Products
              </h3>
              {factory.production.products.map((product, idx) => (
                <div key={idx} 
                     className="text-gray-600 bg-white/40 backdrop-blur-sm
                              rounded-lg p-2 border border-white/30">
                  {product}
                </div>
              ))}
            </div>
            
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-800 text-lg pb-2">
                Capacity
              </h3>
              {factory.production.capacity.map((capacity, idx) => (
                <div key={idx} 
                     className="text-gray-600 bg-white/40 backdrop-blur-sm
                              rounded-lg p-2 border border-white/30">
                  {capacity}
                </div>
              ))}
            </div>
            
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-800 text-lg pb-2">
                Lines
              </h3>
              {factory.production.lines.map((line, idx) => (
                <div key={idx} 
                     className="text-gray-600 bg-white/40 backdrop-blur-sm
                              rounded-lg p-2 border border-white/30">
                  {line}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  </motion.div>
);

const InteractiveMap = () => {
  const [activeLocation, setActiveLocation] = useState(null);
  const [selectedFactory, setSelectedFactory] = useState(null);
  const { scrollYProgress } = useScroll();
  const headerY = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);
  const headerOpacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white relative overflow-hidden bg-gradient-to-b from-white via-orange-50/30 to-white">
      <motion.div 
        style={{ y: headerY, opacity: headerOpacity }}
        className="absolute inset-0 pointer-events-none"
      >
        <div className="absolute inset-0 bg-gradient-to-b from-orange-50/20 to-transparent" />
      </motion.div>

      <div className="container mx-auto px-4">
        <div className="flex flex-col lg:flex-row items-center min-h-screen">
          <motion.div 
            className="lg:w-1/2 py-12 lg:py-0"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-5xl lg:text-7xl font-bold text-gray-900 leading-tight">
              Manufacturing{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-orange-600">
                Excellence
              </span>
            </h1>
            <p className="mt-6 text-lg text-gray-600 max-w-md">
              Discover our state-of-the-art manufacturing facilities across India, delivering innovation at scale.
            </p>
          </motion.div>

          <motion.div 
            className="lg:w-2/3 relative"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="relative aspect-square max-w-3xl mx-auto">
              <img
                src={map}
                alt="India Map"
                className="w-full h-full object-contain"
              />
              {factories.map((factory) => (
                <LocationMarker
                  key={factory.id}
                  location={factory}
                  isActive={activeLocation?.id === factory.id}
                  onClick={() => setSelectedFactory(factory)}
                  onMouseEnter={() => setActiveLocation(factory)}
                  onMouseLeave={() => setActiveLocation(null)}
                />
              ))}
              
            </div>
          </motion.div>
        </div>
      </div>

      <AnimatePresence>
        {selectedFactory && (
          <FactoryDialog 
            factory={selectedFactory} 
            onClose={() => setSelectedFactory(null)} 
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default InteractiveMap;